import React, { useState } from 'react';
import type {
  SelectChangeEvent} from '@mui/material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import PublishIcon from '@mui/icons-material/Publish';
import ReviewsIcon from '@mui/icons-material/RateReview';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { userService } from '../services/userService';
import { lessonService } from '../services/lessonService';
import type { User, UserRole } from '../types';

const ROLE_COLORS: Record<UserRole, 'error' | 'secondary' | 'primary'> = {
  admin: 'error',
  content_manager: 'secondary',
  author: 'primary',
};

const userSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Valid email required'),
  role: z.enum(['admin', 'content_manager', 'author']),
});
type UserForm = z.infer<typeof userSchema>;

const AnalyticsCard: React.FC<{
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color?: string;
}> = ({ label, value, icon, color = '#003359' }) => (
  <Card
    elevation={0}
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      height: '100%',
    }}
  >
    <CardContent
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 2.5,
        '&:last-child': { pb: 2.5 },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(color, 0.12),
          color: color,
        }}
      >
        {icon}
      </Box>
      <Typography variant="h4" fontWeight={800} mt={1}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </CardContent>
  </Card>
);

export const AdminDashboard: React.FC = () => {
  const queryClient = useQueryClient();

  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);
  const [editRole, setEditRole] = useState<UserRole>('author');

  const {
    data: users = [],
    isLoading: isUsersLoading,
    isError: isUsersError,
  } = useQuery({
    queryKey: ['users'],
    queryFn: userService.getAll,
  });

  const { data: lessonsData } = useQuery({
    queryKey: ['lessons', 'admin-stats'],
    queryFn: () => lessonService.getAll({ per_page: 1 }),
  });

  const { data: publishedData } = useQuery({
    queryKey: ['lessons', 'published-count'],
    queryFn: () => lessonService.getAll({ status: 'published', per_page: 1 }),
  });

  const { data: reviewData } = useQuery({
    queryKey: ['lessons', 'review-count'],
    queryFn: () => lessonService.getAll({ status: 'in_review', per_page: 1 }),
  });

  const {
    control,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = useForm<UserForm>({
    resolver: zodResolver(userSchema),
    defaultValues: { name: '', email: '', role: 'author' },
  });

  const createMutation = useMutation({
    mutationFn: userService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setCreateUserOpen(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: UserRole }) =>
      userService.update(id, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditUser(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => userService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteUser(null);
    },
  });

  const userColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 140 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 180 },
    {
      field: 'role',
      headerName: 'Role',
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={(params.value as string).replace('_', ' ')}
          size="small"
          color={ROLE_COLORS[params.value as UserRole]}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueFormatter: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      field: 'last_login',
      headerName: 'Last Login',
      width: 120,
      valueFormatter: (value: string | undefined) =>
        value ? new Date(value).toLocaleDateString() : 'Never',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit Role">
            <IconButton
              size="small"
              onClick={() => {
                setEditUser(params.row as User);
                setEditRole((params.row as User).role);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeleteUser(params.row as User)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const totalLessons = lessonsData?.meta?.total ?? 0;
  const publishedCount = publishedData?.meta?.total ?? 0;
  const reviewCount = reviewData?.meta?.total ?? 0;
  const approvalRate =
    totalLessons > 0 ? Math.round((publishedCount / totalLessons) * 100) : 0;

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Admin Dashboard
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Platform management &bull; Users &bull; Lessons
        </Typography>
      </Box>

      {/* Analytics section */}
      <Box sx={{ px: 3 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}
        >
          Platform Overview
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <AnalyticsCard
              label="Total Users"
              value={users.length}
              icon={<PeopleIcon />}
              color="#003359"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AnalyticsCard
              label="Total Lessons"
              value={totalLessons}
              icon={<SchoolIcon />}
              color="#098198"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AnalyticsCard
              label="Published"
              value={publishedCount}
              icon={<PublishIcon />}
              color="#0E8345"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AnalyticsCard
              label="In Review"
              value={reviewCount}
              icon={<ReviewsIcon />}
              color="#B76E00"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AnalyticsCard
              label="Approval Rate"
              value={`${approvalRate}%`}
              icon={<SchoolIcon />}
              color="#0A64C9"
            />
          </Grid>
        </Grid>
      </Box>

      {/* User Management section */}
      <Box sx={{ px: 3, mt: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            User Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateUserOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            Add User
          </Button>
        </Box>

        {isUsersError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load users.
          </Alert>
        )}

        {isUsersLoading ? (
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        ) : (
          <Box sx={{ height: 480, width: '100%' }}>
            <DataGrid
              rows={users}
              columns={userColumns}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              disableRowSelectionOnClick
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: 'background.default',
                  fontWeight: 700,
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Create User Dialog */}
      <Dialog open={createUserOpen} onClose={() => setCreateUserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box>
            Add New User
            <Typography variant="caption" color="text.secondary" display="block">
              Fill in the details to create a new platform user
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            id="create-user-form"
            onSubmit={handleSubmit((data) => createMutation.mutate(data))}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
          >
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Full Name"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              )}
            />
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.role}>
                  <InputLabel>Role</InputLabel>
                  <Select {...field} label="Role">
                    <MenuItem value="author">Author</MenuItem>
                    <MenuItem value="content_manager">Content Manager</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
            {createMutation.isError && (
              <Alert severity="error">Failed to create user.</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateUserOpen(false)}>Cancel</Button>
          <Button
            type="submit"
            form="create-user-form"
            variant="contained"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!editUser} onClose={() => setEditUser(null)}>
        <DialogTitle>
          <Box>
            Edit Role
            <Typography variant="caption" color="text.secondary" display="block">
              Changing role for {editUser?.name}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={editRole}
              onChange={(e: SelectChangeEvent) => setEditRole(e.target.value as UserRole)}
            >
              <MenuItem value="author">Author</MenuItem>
              <MenuItem value="content_manager">Content Manager</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => editUser && updateMutation.mutate({ id: editUser.id, role: editRole })}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={!!deleteUser} onClose={() => setDeleteUser(null)}>
        <DialogTitle>
          <Box>
            Delete User
            <Typography variant="caption" color="text.secondary" display="block">
              This action cannot be undone
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {deleteUser?.name} ({deleteUser?.email})? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUser(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteUser && deleteMutation.mutate(deleteUser.id)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
